import { Clipboard, Layers2, Link, Store } from 'lucide-react';
import { FC, memo, useEffect, useRef, useState } from 'react';

import { IdParamsType } from 'utils/types';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { ROUTES } from 'features/routes';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { useTranslation } from 'react-i18next';
import styles from './EventActionButtons.module.css';

/** How long "Copy link" shows its optimistic "Copied!" label before reverting. */
const COPIED_FEEDBACK_MS = 2000;

/**
 * Tighter side padding (shared modifier, see src/styles/punchup-button.css). The stock
 * primary/secondary 48px sides made this five-button row ~850px wide, so it wrapped below the
 * show title on mid-width viewports; compact hugs each label + icon and keeps the row beside
 * the title. It is not applied to the icon-only button, which is already a padding-free square.
 */
const COMPACT = 'punchupButtonCompact';
/**
 * Compact, plus the label collapses to icon-only below 768px (shared modifier, same file).
 * Every labelled button in this row therefore carries a leading icon and an explicit aria-label —
 * the visible text is `display: none` on mobile and cannot supply the accessible name there.
 */
const ICON_COLLAPSE = `${COMPACT} punchupButtonIconCollapse`;
/** Never renders its label — the clipboard button is icon-only at every viewport. */
const ICON_ONLY = 'punchupButtonIconOnly';

interface EventActionButtonsProps {
	/**
	 * Show name — the download filename for the parked Download Settlement Report action
	 * (TIC-531). Nothing reads it while that action stays hidden; it is kept on the props so
	 * restoring the action does not mean re-threading a prop through PunchupDashboard.
	 */
	eventTitle: string;
}

// eventTitle is deliberately not destructured — see EventActionButtonsProps.
const EventActionButtonsInner: FC<EventActionButtonsProps> = () => {
	const { t } = useTranslation();
	const { id: eventId }: IdParamsType = useParams();
	const { push } = useHistory();

	// Optimistic feedback only: the host owns the clipboard, so the swap confirms the request was
	// sent, not that the copy succeeded.
	const [hasCopiedLink, setHasCopiedLink] = useState(false);
	const copiedTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	useEffect(() => () => clearTimeout(copiedTimeoutRef.current), []);

	const postToHost = (action: string) => window.parent.postMessage({ action, eventId }, '*');

	const onClickDuplicate = () => postToHost('event-duplicated');
	const onClickCopyEventId = () => postToHost('event-id-copied');
	const onClickCopyShowUrl = () => {
		postToHost('event-url-copied');
		setHasCopiedLink(true);
		clearTimeout(copiedTimeoutRef.current);
		copiedTimeoutRef.current = setTimeout(() => setHasCopiedLink(false), COPIED_FEEDBACK_MS);
	};
	// In-app navigation to the green Box Office sub-page (same in-iframe route the legacy PunchupEventInfo
	// uses) — an SPA route change, not a postMessage and not an external window.open; see CLAUDE.md invariant 1.
	const onClickBoxOffice = () => push(generatePath(ROUTES.BOX_OFFICE, { id: eventId }));

	const boxOfficeLabel = t('dashboard-action-box-office', 'Launch box office');
	const duplicateLabel = t('dashboard-action-duplicate', 'Duplicate');
	const copyLinkLabel = hasCopiedLink
		? t('dashboard-action-copy-link-copied', 'Copied!')
		: t('dashboard-action-copy-link', 'Copy link');
	const copyShowIdLabel = t('dashboard-action-copy-show-id', 'Copy show ID');

	return (
		<div className={styles.container}>
			<PunchupButton
				variant="primary"
				className={ICON_COLLAPSE}
				icon={<Store size={16} aria-hidden />}
				aria-label={boxOfficeLabel}
				onClick={onClickBoxOffice}
				type="button"
			>
				<span className="punchupButtonLabel">{boxOfficeLabel}</span>
			</PunchupButton>

			<PunchupButton
				variant="secondary"
				className={ICON_COLLAPSE}
				icon={<Layers2 size={16} aria-hidden />}
				aria-label={duplicateLabel}
				onClick={onClickDuplicate}
				type="button"
			>
				<span className="punchupButtonLabel">{duplicateLabel}</span>
			</PunchupButton>

			<PunchupButton
				variant="secondary"
				className={ICON_COLLAPSE}
				icon={<Link size={16} aria-hidden />}
				aria-label={copyLinkLabel}
				onClick={onClickCopyShowUrl}
				type="button"
			>
				<span className="punchupButtonLabel">{copyLinkLabel}</span>
			</PunchupButton>

			{/* Icon-only at every viewport: aria-label carries the accessible name, title the hover hint. */}
			<PunchupButton
				variant="secondary"
				className={ICON_ONLY}
				icon={<Clipboard size={16} aria-hidden />}
				aria-label={copyShowIdLabel}
				title={copyShowIdLabel}
				onClick={onClickCopyEventId}
				type="button"
			>
				<span className="punchupButtonLabel">{copyShowIdLabel}</span>
			</PunchupButton>
		</div>
	);
};

export const EventActionButtons = memo(EventActionButtonsInner);
